import { Injectable } from '@angular/core';
import { AuthenticatedHttpService } from './authenticatedhttp.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { CookieService } from "angular2-cookie/services/cookies.service";

import { Quote } from '../model/quote';
import { Retailer } from '../model/retailer';
import { Facility } from '../model/facility';
import { InvalidQuote } from '../model/invalid-quote';
import { QuoteApprovalSearch } from '../model/quote-approval-search';
import { QuoteManagement } from '../model/quote-management';

import { ServiceUtilities } from './service-utilities';
import { QuoteService } from './quote.service';

import * as _ from 'lodash';

@Injectable()
export class QuoteApprovalService {
    private baseUrl;
    private _quoteCache: Quote[];
    private _retailerCache: Retailer[];
    showInvalidQuoteApprovalModal$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    invalidQuote$: BehaviorSubject<InvalidQuote> = new BehaviorSubject(new InvalidQuote());

    constructor(
        private _http: AuthenticatedHttpService,
        private serviceUtils: ServiceUtilities,
        private quoteService: QuoteService,
        private cookieService: CookieService,
    ) {
        this.baseUrl = serviceUtils.getBaseURLFromCookie();
    }

    /// Gets the Quote Approvals for a Plant
    getQuoteApprovals(plantId: number, quoteStatusesId: number): Observable<Quote[]> {
        //let options = this.serviceUtils.getStandardHeaderOptions();
        return this._http.get(`${this.baseUrl}/api/approval/${Number(plantId)}/${quoteStatusesId}`)
            .map(x => {
                let dbQuotes = x.json();
                let mappedQuotes = new Array<Quote>();
                let retailers = new Array<Retailer>();
                if (dbQuotes !== undefined && dbQuotes !== null && dbQuotes.length > 0) {
                    dbQuotes.forEach(quote => {
                        mappedQuotes.push(this.quoteService.mapQuote(quote));
                    });
                    this._quoteCache = mappedQuotes;
                    mappedQuotes.forEach((quote) => {
                        if (retailers === undefined || retailers === null || retailers.length === 0 || !retailers.find(x => x.RetailerNumber === quote.retailerNumber)) {
                            let retailer = new Retailer();
                            retailer.RetailerName = quote.retailerName;
                            retailer.RetailerNumber = quote.retailerNumber;
                            retailers.push(retailer);
                        }
                    });
                }
                this._retailerCache = retailers;
                return this._quoteCache;
            })
            .catch(this._http.handleError);
	}

	/// Gets the Quote Approvals for a Plant
	getQuoteApprovalsWithPaging(plantNumber: number, pageRequested: number = 1, recordsPerPage: number = 20, retailerNumber: string, quoteStatusId: number, searchString: string): Observable<QuoteManagement> {
		let quoteApprovalSearch = new QuoteApprovalSearch();
		quoteApprovalSearch.plantNumber = plantNumber;
		quoteApprovalSearch.retailerNumber = retailerNumber;
		quoteApprovalSearch.quoteStatusID = quoteStatusId;
		quoteApprovalSearch.searchString = searchString;
		quoteApprovalSearch.pageRequested = pageRequested;
		quoteApprovalSearch.recordsPerPage = undefined;

		return this._http.put(`${this.baseUrl}/api/approval/search`, JSON.stringify(quoteApprovalSearch))
			.map(x => {
				this._quoteCache = new Array<Quote>();
				let mappedQuotes = new Array<Quote>();
				let retailers = new Array<Retailer>();
				let dbQuotes = new Array<Quote>();

				let quoteManagement = x.json();
				if (quoteManagement.QuoteList) { dbQuotes = quoteManagement.QuoteList; }
				if (quoteManagement.RetailerList) { retailers = quoteManagement.RetailerList; }

				if (dbQuotes !== undefined && dbQuotes !== null && dbQuotes.length > 0) {
					dbQuotes.forEach(quote => {
						mappedQuotes.push(this.quoteService.mapQuote(quote));
					});
					this._quoteCache = mappedQuotes;
				}
				this._retailerCache = retailers;
				return quoteManagement;
			})
			.catch(this._http.handleError);
	}


    /// Checks the Plants to see if they have Quotes. Returns new List with Plants that have Quotes
    getPlantsWithQuotes(plants: Facility[], quoteStatusesId: number): Observable<Facility[]> {
        return this._http.put(`${this.baseUrl}/api/approval/check-plants-for-quotes/${quoteStatusesId}`, JSON.stringify(plants))
            .map(x => x.json())
            .catch(this._http.handleError);
    }

    /// Validates the ProductLine, Model, and SelectedOptions to see if they are active/exist
    validateQuote(quoteId: number, modifiedBy: string): Observable<InvalidQuote> {
        return this._http.get(`${this.baseUrl}/api/approval/validate-quote/${quoteId}/${modifiedBy}`)
            .map(x => x.json())
            .catch(this._http.handleError);
    }

    sendApprovalEmail(quoteId: number): Observable<any> {
        return this._http.get(`${this.baseUrl}/api/approval/send-approval-status-email/${quoteId}`)
            .map(x => {
                return x;
            })
            .catch(this._http.handleError);
    }

    getQuotesByPlant(plantNumber: number): Quote[] {
        if (this._quoteCache !== undefined && this._quoteCache !== null && this._quoteCache.length > 0) {
            return this._quoteCache.filter(x => x.plantNumber === plantNumber);
        }
        return new Array<Quote>();
    }

    filterQuotes(retailerNumber: string, stat: string): Quote[] {
        let filteredQuotes: Quote[] = this._quoteCache;
        if (filteredQuotes !== undefined && filteredQuotes !== null && filteredQuotes.length > 0) {
            if (retailerNumber !== undefined && retailerNumber !== null && retailerNumber !== '') {
                filteredQuotes = filteredQuotes.filter(x => x.retailerNumber === retailerNumber);
            }
            if (stat != undefined && status != null && stat != '' && stat.toUpperCase() != 'ALL STATUSES') {
                filteredQuotes = stat.toUpperCase().includes('NOT SUBMITTED') ? filteredQuotes.filter(x => x.QuoteStatusesID === 0)
                    : stat.toUpperCase().includes('PENDING APPROVAL') ? filteredQuotes.filter(x => x.QuoteStatusesID === 1)
                        : stat.toUpperCase().includes('DECLINED') ? filteredQuotes.filter(x => x.QuoteStatusesID === 2)
                            : filteredQuotes.filter(x => x.QuoteStatusesID === 3);
            }
        }
        return filteredQuotes;
    }

    sendSubmitApprovalEmail(id: number, email: string) {
        let baseUrl = this.cookieService.get('mchBaseUrl');
        let body: any = {
            quoteId: id,
            baseUrl: baseUrl,
            salesEmail: email
        }
        //let options = this.serviceUtils.getStandardHeaderOptions();
        return this._http.post(`${this.baseUrl}/api/approval/send-quote-submitted-for-approval-email`, body)
            .catch(this._http.handleError);
    }

    get getQuotesCache(): Quote[] {
        if (this._quoteCache !== undefined && this._quoteCache !== null && this._quoteCache.length > 0) {
            return this._quoteCache;
        }
        return new Array<Quote>();
    }

    get getRetailersCache(): Retailer[] {
        if (this._retailerCache !== undefined && this._retailerCache !== null && this._retailerCache.length > 0) {
            return this._retailerCache;
        }
        return new Array<Retailer>();
    }
}




// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/services/quote-approval.service.ts